/** @jsxImportSource @emotion/react */
import {
  Card,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useAsync } from 'react-use';
import { QBLoader } from 'components';
import { getProfileHistory } from '../../../api';
import { HistoryDifferenceCard } from './HistoryDifferenceCard';
import { OnboardingTable } from './OnboardingTable';

interface Props {
  userReference: string;
}

export const History = ({ userReference }: Props) => {
  const { value: history, loading } = useAsync(async () => {
    return getProfileHistory(userReference);
  }, [userReference]);
  const { spacing, palette, shape } = useTheme();

  const [tab, setTab] = useState<'profile' | 'onboarding'>('profile');

  return (
    <>
      {loading && <QBLoader />}
      {history && (
        <>
          <div css={{ marginBottom: spacing.xxs }}>
            <ToggleButtonGroup
              onChange={(option) => setTab(option as 'profile' | 'onboarding')}
              selected={tab}
              toggleOptions={[
                { label: 'Profile & KYC', value: 'profile' },
                { label: 'Onboarding', value: 'onboarding' },
              ]}
            />
          </div>
          <div
            css={[
              {
                display: 'grid',
                gap: spacing.xxs,
                position: 'relative',
                gridTemplateColumns: tab === 'profile' ? '1fr 1fr' : '1fr',
              },
            ]}
          >
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xxs,
                padding: spacing.xxxs,
                borderRadius: shape.borderRadius,
                background: palette.grey.x50,
              }}
            >
              {tab === 'profile' && (
                <>
                  <Typography variant="heading2">Profile History</Typography>
                  {history.profileHistory.map((record, idx) => (
                    <HistoryDifferenceCard
                      key={idx}
                      record={record}
                      previousRecord={
                        idx + 1 >= history.profileHistory.length
                          ? undefined
                          : history.profileHistory[idx + 1]
                      }
                    />
                  ))}
                </>
              )}
            </div>

            {tab === 'onboarding' && (
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.xxs,
                  padding: spacing.xxxs,
                  borderRadius: shape.borderRadius,
                  background: palette.grey.x50,
                }}
              >
                <Typography variant="heading2">Onboarding History</Typography>
                {history.onboardingHistory.map((onboarding, idx) => (
                  <Card key={idx}>
                    <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography>
                        {onboarding.createdAt.toLocaleString()}
                      </Typography>
                    </div>
                    <OnboardingTable onboarding={onboarding} />
                  </Card>
                ))}
              </div>
            )}
            {tab === 'profile' && (
              <>
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.xxs,
                    padding: spacing.xxxs,
                    borderRadius: shape.borderRadius,
                    background: palette.grey.x50,
                  }}
                >
                  <Typography variant="heading2">KYC History</Typography>
                  {history.kycHistory.map((record, idx) => (
                    <HistoryDifferenceCard
                      key={idx}
                      record={record}
                      previousRecord={
                        idx + 1 >= history.kycHistory.length
                          ? undefined
                          : history.kycHistory[idx + 1]
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
